/**
 * kaching-sync.js — the ONLY file that knows about Kaching Bundles.
 *
 * Repairs a one-sided desync in the app's own variant handling. Kaching syncs with the theme in
 * both directions already:
 *   widget → theme   it clicks our size button (needs data-product-option / data-value in
 *                    snippets/bb.liquid — that is the handshake, not this file)
 *   theme → widget   it property-watches input[name="id"] and the ?variant= URL
 *
 * ⚠️ THE BUG THIS EXISTS FOR (verified against kaching-bundles-1955, 2026-08-25). Their
 * _handleNativeVariantChange sets a ~500ms lockout after ANY variant change and DROPS every
 * theme-side change that lands inside it — no queue, no retry, no catch-up:
 *
 *     } else if (this._dealBlockElement.dataset.nativeVariantChangeInProgress
 *                || window.kachingBundlesCurrentVariantChangeInProgress) {
 *       h("handleNativeVariantChange", "skipping");
 *       return;
 *     }
 *
 * The lockout is their loop-breaker (widget clicks our button → our button writes the form → widget
 * reacts → …), so it has to exist. What is missing is the trailing re-check. Two ways a shopper
 * hits it: clicking sizes faster than 500ms apart, or clicking a size just after selecting a deal
 * bar (the bar selection is itself a variant change, so it arms the lockout).
 *
 * NOT cosmetic: the cart line follows the WIDGET, not our form — confirmed by a real add-to-cart.
 * A shopper could see L in the size row and be shipped XS.
 *
 * WHY WE CANNOT JUST RE-SEND THE VARIANT: their first guard is `t != this._currentVariantId`, and
 * _currentVariantId IS updated before the skip — only the UI is not. So the app already believes it
 * is on the new variant and ignores a repeat. The repair has to write their render state directly.
 *
 * HOW: `current-variant-id` on <kaching-bundles-block> is a Svelte custom-element attribute, mapped
 * straight to a prop, so setting it re-renders the widget (verified: XS,XS,XS → L,L,XS, with our own
 * buttons unmoved). We only ever write it when the widget and the form actually disagree, so on the
 * normal path this file does nothing at all.
 *
 * ⚠️ UNDOCUMENTED HOOK. `current-variant-id` is app internals, not a public API (window.
 * kachingBundlesApi exposes only fetchDeals). It can vanish in any app update — and if it does this
 * file degrades to a no-op rather than breaking the page. Re-verify on app updates: pick two sizes
 * ~150ms apart with SHOP THE SET selected, then confirm the widget shows the second one.
 *
 * ── WHY THIS IS PER-BUY-BOX AND NOT PDP-ONLY ──────────────────────────────────────────────────
 * snippets/bb.liquid is shared, so the quick-view drawer renders its own buy box AND its own
 * <kaching-bundle>. A drawer product with a deal therefore gets a real second widget on the page.
 * Verified live (skirt PDP + NAVY SAILOR WINDBREAKER in the drawer): both initialise, both render
 * their own bars, and they do NOT interfere — Kaching is multi-instance by design, and the lockout
 * flag that causes the bug is PER ELEMENT (`dealBlockElement.dataset.nativeVariantChangeInProgress`),
 * not global. (`window.kachingBundlesCurrentVariantChangeInProgress` is global but is only armed on
 * the widget → theme direction.)
 *
 * The second widget has the SAME dropped-change bug, so it needs the same repair — measured:
 * a 3-click burst in the drawer left form=L / widget=M and it never recovered. Hence: every buy box
 * repairs ITS OWN widget, resolved structurally by containment. No context special-casing, and
 * nothing here can ever write one product's variant into another product's widget.
 *
 * Plain IIFE + <script defer>, loaded from blocks/buy-box.liquid AND snippets/quick-view-drawer.liquid
 * — same shape, and the same idempotence guard, as stoq-notify.js.
 */
(function () {
  // Idempotence guard — see the same note in stoq-notify.js. This file now loads from BOTH
  // blocks/buy-box.liquid and snippets/quick-view-drawer.liquid, so on the PDP it executes twice.
  //
  // A double-run here is less dangerous than it looks — two copies means two independent debounce
  // timers per buy box, but reconcile() is idempotent (it returns early once the widget and the form
  // agree), so the second write is a no-op. The guard is still the right call: it keeps the timer
  // bookkeeping single-owner and stops two retry chains racing inside the app's ~500ms lockout,
  // which is the window this whole file exists to work around.
  if (window.__ssKachingSync) return;
  window.__ssKachingSync = true;

  // Their lockout is 500ms (1000ms if the `remove_variant_change_delay` feature flag is ever turned
  // on for this shop). Wait past the longer one, so this keeps working if that flag flips.
  var SETTLE_MS = 1100;
  // One retry, in case the widget was mid-render when we first looked.
  var RETRY_MS = 400;

  // Debounce timers keyed by the buy-box element, so a burst in the drawer and a burst on the PDP
  // never share (or cancel) each other's timer.
  var timers = new WeakMap();

  function reconcile(buyBox, attempt) {
    // The drawer replaces its whole content on every open / swatch-hop, so a timer scheduled before
    // that can fire against a detached buy box. Writing to it would be harmless but pointless.
    if (!buyBox.isConnected) return;

    // Scoped by CONTAINMENT — this buy box's own widget, never "the first one in the document".
    var block = buyBox.querySelector('kaching-bundles-block');
    // No widget for this product (no deal targets it), or the app is gone → nothing to repair.
    if (!block) return;

    var input = buyBox.querySelector('[data-bb-id]');
    var expected = input && input.value;
    if (!expected) return;

    if (block.getAttribute('current-variant-id') === expected) return; // already agreed — no-op

    // Match what the app itself writes on a theme-driven change, so its own bookkeeping stays
    // consistent: source first, then the id that triggers the re-render.
    block.setAttribute('current-variant-source', 'native');
    block.setAttribute('current-variant-id', expected);

    if (attempt < 1) {
      setTimeout(function () {
        reconcile(buyBox, attempt + 1);
      }, RETRY_MS);
    }
  }

  document.addEventListener('sundae:variant-change', function (e) {
    // The event is dispatched ON the buy-box and bubbles, so the originating box is the target.
    var buyBox = e.target && e.target.closest && e.target.closest('buy-box');
    if (!buyBox) return;

    // Debounce per buy box: a burst of fast clicks produces ONE reconcile, for the size the shopper
    // actually landed on. reconcile() re-reads that box's form at fire time rather than trusting the
    // id carried by whichever event happened to win the race.
    clearTimeout(timers.get(buyBox));
    timers.set(
      buyBox,
      setTimeout(function () {
        reconcile(buyBox, 0);
      }, SETTLE_MS)
    );
  });
})();
